import { fetcher } from "@certn/api";
import { Loader, Text } from "@certn/cascadia-react";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { components } from "@certn/api/generated-schemas/applicant-portal";

import { StripeSetupIntentForm } from "./StripeSetupIntentForm";
import { fonts, mapGatewayToStripeKey } from "./constants";
import { createRadarSessionId } from "./radarSession";
import { useStripeAppearance } from "./useStripeAppearance";
import { useUser } from "../../hooks/api/useUser";

interface Props {
  handleClose?: () => void;
  initialPaymentProfileCount: number;
  defaultAddress?: {
    country?: string;
    postal_code?: string;
  };
}

type SetupIntentResponse =
  components["schemas"]["CreateSetupIntentSuccessResponse"];

async function createSetupIntent(
  radarSessionId?: string
): Promise<SetupIntentResponse> {
  const requestBody: components["schemas"]["CreateApplicantSetupIntentRequest"] =
    radarSessionId ? { radar_session: radarSessionId } : {};
  return fetcher.submit<SetupIntentResponse>(
    "/api/internal/applicant/me/setup-intent/",
    requestBody
  );
}

export function StripeSetupIntent(props: Props) {
  const { handleClose, initialPaymentProfileCount, defaultAddress } = props;
  const { details: userDetails } = useUser();
  const { t } = useTranslation();
  const [stripePromise, setStripePromise] =
    useState<Promise<Stripe | null> | null>(null);
  const appearance = useStripeAppearance();

  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [stripeGateway, setStripeGateway] = useState<string | null>(null);
  const [intentEpoch, setIntentEpoch] = useState(0);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const remountSetupIntent = useCallback((message?: string) => {
    setClientSecret(null);
    setStripeGateway(null);
    setStripePromise(null);
    setLoadError(null);
    setFormError(message ?? null);
    setIntentEpoch((epoch) => epoch + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function fetchClientSecret() {
      if (!userDetails?.id) return;
      setLoadError(null);
      try {
        // Probe first without Radar so we learn the exact Stripe gateway
        // (including PREPAY / consumer). Region-default keys attach the
        // session to the wrong account when those gateways are selected
        // (DAT-1731 review).
        const probe = await createSetupIntent();
        if (cancelled) return;
        const gateway = probe.stripeGateway || "";
        const gatewayKey = gateway ? mapGatewayToStripeKey[gateway] : undefined;
        const radarSessionId = await createRadarSessionId(gatewayKey);
        if (cancelled) return;

        if (radarSessionId) {
          try {
            const response = await createSetupIntent(radarSessionId);
            if (cancelled) return;
            setClientSecret(response.clientSecret || "");
            setStripeGateway(response.stripeGateway || gateway);
          } catch (radarCreateError) {
            // Fall back to the probe SetupIntent so card setup still works
            // when the Radar-enriched recreate fails (DAT-1731 Bugbot).
            console.error(radarCreateError);
            if (cancelled) return;
            setClientSecret(probe.clientSecret || "");
            setStripeGateway(gateway);
          }
        } else {
          setClientSecret(probe.clientSecret || "");
          setStripeGateway(gateway);
        }
      } catch (error) {
        console.error(error);
        if (!cancelled) {
          setLoadError(t("Something went wrong"));
        }
      }
    }
    fetchClientSecret();
    return () => {
      cancelled = true;
    };
    // Omit `t` — identity changes on language switch would remint the SetupIntent
    // and remount Elements mid-entry (DAT-1731 Bugbot).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional
  }, [userDetails?.id, intentEpoch]);

  // Memoize the Stripe promise to avoid multiple clients on re-renders
  // Memoize it inside the component to prevent CSP errors for routes that don't use Stripe
  // Use the gateway returned from the backend to ensure we use the correct Stripe account
  useEffect(() => {
    if (!stripeGateway) return;
    const stripeKey = mapGatewayToStripeKey[stripeGateway];
    if (!stripeKey) {
      console.error(`No Stripe key configured for gateway: ${stripeGateway}`);
      return;
    }
    setStripePromise(loadStripe(stripeKey));
  }, [stripeGateway]);

  if (loadError) {
    return <Text style="form/body-secondary">{loadError}</Text>;
  }

  if (!clientSecret || !stripePromise) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "200px",
        }}
      >
        <Loader />
      </div>
    );
  }

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret,
        appearance,
        fonts,
      }}
    >
      <StripeSetupIntentForm
        defaultAddress={defaultAddress}
        handleClose={handleClose}
        initialPaymentProfileCount={initialPaymentProfileCount}
        onSetupIntentConsumed={remountSetupIntent}
        initialErrorMessage={formError}
        clientSecret={clientSecret}
      />
    </Elements>
  );
}
